import { useState } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowUpDown, GripVertical, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type SortDir = "asc" | "desc";

export type SortFieldDef<K extends string> = {
  key: K;
  label: string;
  directions: { asc: string; desc: string };
  defaultDir: SortDir;
};

export type SortEntry<K extends string> = { key: K; dir: SortDir };

type Props<K extends string> = {
  entityLabel: string; // e.g. "parts", "bins"
  fields: readonly SortFieldDef<K>[];
  value: SortEntry<K>[];
  defaultValue: SortEntry<K>[];
  onChange: (next: SortEntry<K>[]) => void;
};

function SortRow<K extends string>({
  entry,
  idx,
  fields,
  used,
  onFieldChange,
  onDirToggle,
  onRemove,
}: {
  entry: SortEntry<K>;
  idx: number;
  fields: readonly SortFieldDef<K>[];
  used: Set<K>;
  onFieldChange: (key: K) => void;
  onDirToggle: () => void;
  onRemove: () => void;
}) {
  const id = `${entry.key}-${idx}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const field = fields.find((f) => f.key === entry.key);
  const opts = fields.filter((f) => !used.has(f.key) || f.key === entry.key);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.9 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing shrink-0 touch-none"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <Select value={entry.key} onValueChange={(v) => onFieldChange(v as K)}>
        <SelectTrigger className="flex-1 h-9">
          <SelectValue>{field?.label}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {opts.map((o) => (
            <SelectItem key={o.key} value={o.key}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        type="button"
        onClick={onDirToggle}
        className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-input bg-background text-sm hover:bg-accent hover:text-accent-foreground whitespace-nowrap shrink-0"
        aria-label="Toggle sort direction"
      >
        {field?.directions[entry.dir]}
        <ArrowUpDown className="w-3.5 h-3.5 opacity-60" />
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="text-muted-foreground hover:text-foreground shrink-0"
        aria-label="Remove sort"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function InventorySortMenu<K extends string>({
  entityLabel,
  fields,
  value,
  defaultValue,
  onChange,
}: Props<K>) {
  const [open, setOpen] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const usedKeys = new Set(value.map((v) => v.key));
  const activeCount = value.length;
  const canAdd = value.length < fields.length;

  const ids = value.map((v, i) => `${v.key}-${i}`);

  const updateFieldAt = (idx: number, key: K) => {
    const field = fields.find((f) => f.key === key);
    const next = [...value];
    next[idx] = { key, dir: field?.defaultDir ?? "asc" };
    onChange(next);
  };

  const toggleDirAt = (idx: number) => {
    const next = [...value];
    next[idx] = { ...next[idx], dir: next[idx].dir === "asc" ? "desc" : "asc" };
    onChange(next);
  };

  const removeAt = (idx: number) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next.length ? next : defaultValue);
  };

  const addSort = () => {
    const next = fields.find((f) => !usedKeys.has(f.key));
    if (next) onChange([...value, { key: next.key, dir: next.defaultDir }]);
  };

  const clearAll = () => onChange(defaultValue);

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onChange(arrayMove(value, from, to));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground hover:bg-muted gap-1.5"
        >
          <ArrowUpDown className="w-4 h-4" />
          Sort{activeCount > 1 ? ` · ${activeCount}` : ""}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0">
        <div className="px-4 pt-3 pb-2 text-sm text-muted-foreground">
          Sort {entityLabel} by
        </div>
        <div className="px-4 pb-3 space-y-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              {value.map((entry, idx) => (
                <SortRow
                  key={ids[idx]}
                  entry={entry}
                  idx={idx}
                  fields={fields}
                  used={usedKeys}
                  onFieldChange={(k) => updateFieldAt(idx, k)}
                  onDirToggle={() => toggleDirAt(idx)}
                  onRemove={() => removeAt(idx)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
        <div className="border-t border-border px-4 py-2 flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={addSort}
            disabled={!canAdd}
            className="gap-1.5"
          >
            <Plus className="w-4 h-4" />
            Add sort
          </Button>
          <button
            type="button"
            onClick={clearAll}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Clear all
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
